import React, { useMemo, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';

import PropertyForm from '~/components/Common/Forms/PropertyForm';
import preparePropertyFormSchema from '~/components/Vendors/Properties/helpers/preparePropertyFormSchema';

import { useGetPropertiesSchema } from '~/hooks/apis/accommodation/useGetProperty';
import { useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { isSalesforceOpportunityId } from '~/utils/validateSalesforceId';

import {
  getMissingRequiredPropertyFields,
  getNextButtonLabel,
  mapExistingPropertyLogoToPropertyFormData,
  mapExtraGuestSurchargeAgeRangesToPropertyFormData,
  mapVendorBillingToPropertyFormData,
} from './utils';
import type { VendorBillingDetails } from './utils';

const STEPS = [
  {
    label: 'Salesforce ID',
    title: 'Step 1: Add offer ID from Salesforce',
    helperText: 'Enter the Salesforce offer ID to start the set up flow.',
  },
  {
    label: 'Property',
    title: 'Step 2: Check or create property',
    helperText: 'Use an existing property, or create a new property using defaults.',
  },
  {
    label: 'Rooms',
    title: 'Step 3: Create rooms on property',
    helperText: 'Room details will be added here.',
  },
  {
    label: 'Rate plan',
    title: 'Step 4: Create Flash Rate Plan',
    helperText: 'Rate plan details will be added here.',
  },
  {
    label: 'Room rates',
    title: 'Step 5: Apply Rate Plan to every room',
    helperText: 'The new Flash Rate Plan will be applied to each room.',
  },
  {
    label: 'Taxes & fees',
    title: 'Step 6: Taxes and Fees',
    helperText: 'Taxes and fees will be selected from the country rules.',
  },
  {
    label: 'Content',
    title: 'Step 7: Go to Content page',
    helperText: 'Content information will be reviewed on the Content page.',
  },
];

const CREATE_NEW_PROPERTY = 'create-new-property';
const PROPERTY_FORM_ID = 'flash-offer-property-form';

interface Props {
  disabled?: boolean;
  properties?: Array<App.Property>;
  vendor?: (App.Vendor & VendorBillingDetails) | null;
  vendorId: string;
}

export default function SetUpFlashOfferSection({ disabled, properties = [], vendor, vendorId }: Props) {
  const defaultPropertyId = properties.find((property) => !property.deleted_at)?.id ?? CREATE_NEW_PROPERTY;
  const [salesforceOfferId, setSalesforceOfferId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isExitConfirmationOpen, setIsExitConfirmationOpen] = useState(false);
  const [isCreatingNewProperty, setIsCreatingNewProperty] = useState(false);
  const [isValidatingOpportunity, setIsValidatingOpportunity] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedPropertyId, setSelectedPropertyId] = useState(defaultPropertyId);
  const [createdProperty, setCreatedProperty] = useState<App.Property | null>(null);
  const [propertyFormData, setPropertyFormData] = useState<Record<string, unknown>>({});
  const validationRunId = useRef(0);
  const trimmedSalesforceOfferId = salesforceOfferId.trim();
  const isCreatingProperty = selectedPropertyId === CREATE_NEW_PROPERTY;
  const isPropertyStep = activeStep === 1;
  const isCreatePropertyStep = isPropertyStep && isCreatingProperty;
  const { refetch: refetchOpportunity } = useGetOpportunity(trimmedSalesforceOfferId);
  const { refetch: refetchOpportunityRelated } = useGetOpportunityRelated(trimmedSalesforceOfferId, { enabled: false });
  const {
    data: createPropertySchema,
    isError: hasCreatePropertySchemaError,
    isFetching: isLoadingCreatePropertySchema,
    refetch: refetchCreatePropertySchema,
  } = useGetPropertiesSchema(activeStep === 1 && isCreatingProperty);

  const currentStep = STEPS[activeStep];
  const selectableProperties =
    !createdProperty || properties.some((property) => property.id === createdProperty.id)
      ? properties
      : [...properties, createdProperty];
  const activeProperties = selectableProperties.filter((property) => !property.deleted_at);
  const propertySchema = useMemo(
    () => preparePropertyFormSchema(createPropertySchema, propertyFormData),
    [createPropertySchema, propertyFormData],
  );
  const isLoadingPropertyForm = isCreatingProperty && isLoadingCreatePropertySchema;
  const hasPropertyFormError = isCreatingProperty && hasCreatePropertySchemaError;
  const missingRequiredPropertyFields = getMissingRequiredPropertyFields({
    isCreatePropertyStep,
    propertyFormData,
    propertySchema,
  });
  const hasMissingRequiredPropertyFields = missingRequiredPropertyFields.length > 0;
  const isPropertyStepNextDisabled =
    isCreatePropertyStep &&
    (isLoadingPropertyForm || hasPropertyFormError || !propertySchema || hasMissingRequiredPropertyFields);
  const nextButtonLabel = getNextButtonLabel({
    isCreatePropertyStep,
    isCreatingNewProperty,
    isValidatingOpportunity,
  });

  function handleOpen() {
    setIsDialogOpen(true);
    setIsExitConfirmationOpen(false);
    setIsCreatingNewProperty(false);
    setIsValidatingOpportunity(false);
    validationRunId.current += 1;
    setSalesforceOfferId('');
    setErrorMessage(null);
    setActiveStep(0);
    setSelectedPropertyId(defaultPropertyId);
    setCreatedProperty(null);
    setPropertyFormData({});
  }

  function handleClose() {
    if (activeStep === 0 && !trimmedSalesforceOfferId) {
      handleConfirmClose();
      return;
    }

    setIsExitConfirmationOpen(true);
  }

  function handleCancelClose() {
    setIsExitConfirmationOpen(false);
  }

  function handleConfirmClose() {
    validationRunId.current += 1;
    setIsCreatingNewProperty(false);
    setIsValidatingOpportunity(false);
    setIsExitConfirmationOpen(false);
    setIsDialogOpen(false);
  }

  function handlePrevious() {
    setErrorMessage(null);
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }

  function handlePropertyCreateStart() {
    setErrorMessage(null);
    setIsCreatingNewProperty(true);
  }

  function handlePropertyCreateError() {
    setIsCreatingNewProperty(false);
    setErrorMessage('Unable to create property.');
  }

  function handlePropertyCreateSuccess(property: App.Property) {
    if (!property?.id) {
      handlePropertyCreateError();
      return;
    }

    setCreatedProperty(property);
    setSelectedPropertyId(property.id);
    setIsCreatingNewProperty(false);
    setErrorMessage(null);
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  }

  async function handleNext() {
    if (activeStep === 0 && !isSalesforceOpportunityId(trimmedSalesforceOfferId)) {
      setErrorMessage('Enter a valid Salesforce offer ID.');
      return;
    }

    if (activeStep === 0) {
      const runId = validationRunId.current + 1;
      validationRunId.current = runId;
      setIsValidatingOpportunity(true);
      const { data: opportunity, error } = await refetchOpportunity();

      if (runId !== validationRunId.current) {
        return;
      }

      if (error || !opportunity?.account_18_digit_id__c) {
        setIsValidatingOpportunity(false);
        setErrorMessage('Unable to validate the Salesforce offer ID.');
        return;
      }

      const opportunityVendorId = opportunity.account_18_digit_id__c;

      if (opportunityVendorId !== vendorId) {
        setIsValidatingOpportunity(false);
        setErrorMessage('This Salesforce offer does not belong to this vendor.');
        return;
      }

      const { data: opportunityRelated } = await refetchOpportunityRelated().catch(() => ({ data: undefined }));

      if (runId !== validationRunId.current) {
        return;
      }

      setPropertyFormData({
        id_salesforce_external: vendorId,
        ...mapVendorBillingToPropertyFormData(vendor),
        ...mapExistingPropertyLogoToPropertyFormData(properties),
        ...mapExtraGuestSurchargeAgeRangesToPropertyFormData(opportunityRelated?.extra_guest_surcharges),
      });
      setIsValidatingOpportunity(false);
    }

    if (activeStep === 1 && isCreatingNewProperty) {
      return;
    }

    if (activeStep === 1 && isLoadingPropertyForm) {
      setErrorMessage('Wait for property details to finish loading.');
      return;
    }

    if (activeStep === 1 && missingRequiredPropertyFields.length > 0) {
      setErrorMessage(`Complete mandatory property fields: ${missingRequiredPropertyFields.join(', ')}.`);
      return;
    }

    if (activeStep === 1 && isCreatingProperty && !propertySchema) {
      setErrorMessage('Unable to validate property details.');
      return;
    }

    setErrorMessage(null);
    setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  }

  return (
    <>
      <Button variant="contained" onClick={handleOpen} disabled={disabled}>
        Set up Flash offer
      </Button>

      <Dialog open={isDialogOpen} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center' }}>
          Set up Flash offer
          <IconButton aria-label="Close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map((step) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Stack spacing={2}>
            <Typography variant="h6">{currentStep.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {currentStep.helperText}
            </Typography>

            {activeStep === 0 && (
              <TextField
                label="Salesforce offer ID"
                value={salesforceOfferId}
                onChange={(event) => {
                  setSalesforceOfferId(event.target.value);
                  setErrorMessage(null);
                }}
                required
                fullWidth
                autoFocus
              />
            )}

            {activeStep === 1 && (
              <Box sx={{ minHeight: isLoadingPropertyForm ? 560 : undefined }}>
                {activeProperties.length > 0 && (
                  <Stack spacing={2} sx={{ mb: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel id="flash-offer-property-select-label">Property</InputLabel>
                      <Select
                        label="Property"
                        labelId="flash-offer-property-select-label"
                        value={selectedPropertyId}
                        onChange={(event) => {
                          setErrorMessage(null);
                          setSelectedPropertyId(event.target.value);
                        }}
                      >
                        <MenuItem value={CREATE_NEW_PROPERTY}>Create new property</MenuItem>
                        {activeProperties.map((property) => (
                          <MenuItem key={property.id} value={property.id}>
                            {property.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Divider />
                  </Stack>
                )}

                {isLoadingPropertyForm && (
                  <Alert icon={<CircularProgress size={16} />} severity="info">
                    Loading property details.
                  </Alert>
                )}
                {!isLoadingPropertyForm && hasPropertyFormError && (
                  <Alert
                    severity="error"
                    action={
                      <Button
                        onClick={() => {
                          setErrorMessage(null);
                          refetchCreatePropertySchema();
                        }}
                      >
                        Try again
                      </Button>
                    }
                  >
                    Unable to load property details.
                  </Alert>
                )}
                {!isCreatingProperty && (
                  <Alert severity="info">Selected existing property will be used for this Flash offer.</Alert>
                )}
                {isCreatingProperty && !isLoadingPropertyForm && !hasPropertyFormError && propertySchema && (
                  <PropertyForm
                    key="new-property"
                    formId={PROPERTY_FORM_ID}
                    hideActions
                    onDraftChange={setPropertyFormData}
                    onSubmitError={handlePropertyCreateError}
                    onSubmitStart={handlePropertyCreateStart}
                    onSubmitSuccess={handlePropertyCreateSuccess}
                    property={propertyFormData}
                    schema={propertySchema}
                    vendorId={vendorId}
                  />
                )}
              </Box>
            )}

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={handlePrevious} disabled={activeStep === 0}>
            Previous
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={handleClose}>Close</Button>
            {activeStep === STEPS.length - 1 ? (
              <Button variant="contained" onClick={handleConfirmClose}>
                Complete
              </Button>
            ) : (
              <Button
                variant="contained"
                form={isCreatePropertyStep ? PROPERTY_FORM_ID : undefined}
                type={isCreatePropertyStep ? 'submit' : 'button'}
                onClick={isCreatePropertyStep ? undefined : handleNext}
                disabled={
                  (activeStep === 0 && !trimmedSalesforceOfferId) ||
                  isValidatingOpportunity ||
                  isPropertyStepNextDisabled ||
                  isCreatingNewProperty
                }
              >
                {nextButtonLabel}
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={isExitConfirmationOpen} onClose={handleCancelClose}>
        <DialogTitle>Exit Set up Flash offer?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Any information entered in this wizard will be lost and this action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmClose}>
            Exit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
